/**
 * Test/debug hook registry exposed as `window.__bh`.
 *
 * Playwright playtests (and the browser console) drive the game exclusively through these hooks.
 * Each module registers the hooks it owns; later registrations override earlier ones (so e.g. the
 * heat system replaces the scaffold's default `setHeat`).
 *
 * EXTENDING: add the signature via declaration merging, then register in your system's init:
 *
 *   declare module '../core/hooks' {
 *     interface BhHooks {
 *       spawnPolice(x: number, z: number): number;
 *     }
 *   }
 *   ctx.hooks.register('spawnPolice', (x, z) => ...);
 *
 * Snapshot providers add a key to `__bh.snapshot()`:
 *   ctx.hooks.registerSnapshot('traffic', () => ({ cars: n }));
 *
 * Rules: hooks must accept/return plain JSON data (Playwright serialises across the boundary).
 */
export interface BhHooks {
  /** Full JSON snapshot: game state + every registered snapshot provider. */
  snapshot(): Record<string, unknown>;
  /** Move the player (on foot or with their vehicle) to (x, z). */
  teleport(x: number, z: number): void;
  /** Spawn a vehicle by spec id; returns vehicle id. */
  spawnVehicle(type: string, x: number, z: number, heading?: number): number;
  /** Put the player in the given vehicle (driver seat). */
  enterVehicle(vehicleId: number): void;
  exitVehicle(): void;
  /** Force wanted level 0..4. */
  setHeat(level: number): void;
  /** Run n fixed simulation steps synchronously, then render once. */
  stepFrames(n: number): void;
  /** Recent events [{frame, type, payload}] (optionally filtered by type). */
  events(type?: string): { frame: number; type: string; payload: unknown }[];
  clearEvents(): void;
  startMission(id: string): void;
  /** Performance numbers (fps, frame ms, draw calls, triangles, physics ms...). */
  getPerf(): Record<string, number>;
  /** Hold virtual input: e.g. setInput({ throttle: 1, steer: -0.5, handbrake: true }). */
  setInput(input: Record<string, number | boolean>): void;
  /** Release all virtual input. */
  clearInput(): void;
  /** Press an action for one step (e.g. 'enterExit'). */
  press(action: string): void;
  pause(paused: boolean): void;
  setTimeScale(scale: number): void;
  /** List of registered hook names. */
  help(): string[];
}

type AnyFn = (...args: never[]) => unknown;

export class HookRegistry {
  private hooks = new Map<string, AnyFn>();
  private snapshots = new Map<string, () => unknown>();
  /** The live object assigned to window.__bh; registrations are mirrored onto it. */
  readonly api: Record<string, unknown> = {};

  constructor() {
    this.api.help = (): string[] => this.names();
  }

  register<K extends keyof BhHooks>(name: K, fn: BhHooks[K]): void {
    this.hooks.set(name as string, fn as unknown as AnyFn);
    this.api[name as string] = fn;
  }

  registerSnapshot(key: string, provider: () => unknown): void {
    this.snapshots.set(key, provider);
  }

  has(name: keyof BhHooks): boolean {
    return this.hooks.has(name as string);
  }

  call<K extends keyof BhHooks>(name: K, ...args: Parameters<BhHooks[K]>): ReturnType<BhHooks[K]> {
    const fn = this.hooks.get(name as string);
    if (!fn) throw new Error(`__bh.${String(name)} is not registered`);
    return (fn as unknown as (...a: unknown[]) => ReturnType<BhHooks[K]>)(...args);
  }

  collectSnapshot(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, p] of this.snapshots) {
      try {
        out[k] = p();
      } catch (e) {
        out[k] = { error: String(e) };
      }
    }
    return out;
  }

  names(): string[] {
    return [...this.hooks.keys(), 'help'].sort();
  }
}
